import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { AccessControlService } from '../services/auth/access-control.service';

/**
 * Error Interceptor
 * Handles HTTP errors globally
 * - 401: Unauthorized - redirect to login
 * - 403: Forbidden - show access denied page
 * - 500: Server error - log and show error message
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const accessControlService = inject(AccessControlService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An error occurred';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
        console.error('Client-side error:', errorMessage);
      } else {
        // Server-side error
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        console.error('Server-side error:', errorMessage);
        // Log server error body if available (validation errors, details)
        if (error.error) {
          console.error('Server error body:', error.error);
        }

        // Handle specific error codes
        switch (error.status) {
          case 401:
            // Unauthorized - token expired or invalid
            console.warn('Unauthorized access detected');
            
            // Check if this is an authentication endpoint
            const isAuthEndpoint = req.url.includes('/auth/login') || 
                                   req.url.includes('/auth/verify-otp') || 
                                   req.url.includes('/auth/forgot-password') ||
                                   req.url.includes('/auth/reset-password');
            
            // Only auto-logout and redirect if NOT on an auth endpoint
            // This allows error messages to be displayed on login/auth pages
            if (!isAuthEndpoint) {
              authService.logout();
            }
            break;

          case 403:
            // Forbidden - user doesn't have permission
            console.warn('Forbidden access - insufficient permissions');
            const rawMessage = error.error?.message ?? error.error?.error;
            const serverMessage = typeof rawMessage === 'string' ? rawMessage : undefined;
            // e.g. "Missing required privilege: view_doctors" -> ['view_doctors']
            const privileges =
              serverMessage?.match(/privileges?\s*:\s*([a-z0-9_,\s]+)/i)?.[1]
                .split(',')
                .map((p) => p.trim())
                .filter(Boolean) ?? [];
            accessControlService.deny(
              privileges.length ? { privileges } : { message: serverMessage }
            );
            break;

          case 404:
            // Not found
            console.warn('Resource not found');
            break;

          case 500:
            // Internal server error
            console.error('Internal server error');
            break;

          case 0:
            // Network error or CORS issue
            console.error('Network error - unable to reach server');
            errorMessage = 'Unable to connect to server. Please check your internet connection.';
            break;

          default:
            console.error(`Unexpected error: ${error.status}`);
        }
      }

      // You can show a toast/notification here
      // this.notificationService.showError(errorMessage);

      return throwError(() => error);
    })
  );
};
